import type { RegistrationOption } from "@/types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

// ---- Types ----

export interface AiSource {
  id: string;
  name: string;
  url: string;
  type: "website" | "json_api";
  instructions: string;
  enabled: boolean;
  scheduleCron: string | null;
  consecutiveFailures: number;
  lastError: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiCollectionMetadata {
  protocolVersion: 1;
  descriptionUpdatedAt?: string;
  descriptionRunId?: string;
  key: string;
  kind: "event" | "festival" | "exhibition";
  parentKey: string | null;
  start: string | null;
  end: string | null;
  datePrecision: "datetime" | "date" | "unknown";
  isAllDay: boolean;
  sourceURLs: string[];
  imageURLs: string[];
  evidence: string;
  missing: string[];
  priceText: string | null;
  registrationText: string | null;
}

export interface AiWorkerStatus {
  configured: boolean;
  available: boolean;
  lastSeenAt: string | null;
  engine: "codex";
  model: string;
  schedulerEnabled: false;
}

export interface AiEvent {
  id: string;
  sourceId: string;
  sourceName: string;
  runId: string;
  title: string;
  eventDescription: string;
  date: string | null;
  endDate: string | null;
  isAllDay: boolean;
  locationName: string;
  locationAddress: string;
  sourceUrl: string;
  ticketPrice: string | null;
  registrationOptions?: RegistrationOption[];
  registrationNotes?: string;
  registrationType: string | null;
  registrationValue: string | null;
  confidence: number;
  status: string;
  createdAt: string;
  suggestedCategoryId: string | null;
  suggestedVenueId: string | null;
  duplicateOfId: string | null;
  imageUrl: string | null;
  collectionMetadata?: AiCollectionMetadata | null;
  parentProposalId?: string | null;
}

export interface AiRunLog {
  engine?: "codex" | "legacy" | "json_api";
  id: string;
  sourceId: string | null;
  sourceName: string | null;
  trigger: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  eventsFound: number;
  eventsInserted: number;
  eventsSkipped: number;
  pagesVisited: number;
  llmTokensUsed: number;
  llmCostEstimate: number;
  errors: Array<{ source?: string; message: string }> | null;
  skippedEvents: Array<{ source: string; title: string; reason: string }> | null;
  statusMessage: string | null;
}

export function useAiWorkerStatus() {
  return useQuery({
    queryKey: ["ai-agent", "worker"],
    queryFn: () => api.get<AiWorkerStatus>("/admin/ai-agent/worker"),
    refetchInterval: 10000,
    staleTime: 0,
  });
}

// ---- Sources ----

export function useAiSources() {
  return useQuery({
    queryKey: ["ai-agent", "sources"],
    queryFn: () => api.get<{ sources: AiSource[] }>("/admin/ai-agent/sources"),
    select: (data) => data.sources,
  });
}

export function useCreateAiSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      url: string;
      type?: "website" | "json_api";
      instructions?: string;
      enabled?: boolean;
      scheduleCron?: string | null;
    }) => api.post<AiSource>("/admin/ai-agent/sources", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "sources"] }),
  });
}

export function useUpdateAiSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      url?: string;
      type?: "website" | "json_api";
      instructions?: string;
      enabled?: boolean;
      scheduleCron?: string | null;
    }) => api.put<AiSource>(`/admin/ai-agent/sources/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "sources"] }),
  });
}

export function useDeleteAiSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/ai-agent/sources/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "sources"] }),
  });
}

// ---- Runs ----

export function useAiRuns() {
  return useQuery({
    queryKey: ["ai-agent", "runs"],
    queryFn: () => api.get<{ runs: AiRunLog[] }>("/admin/ai-agent/runs"),
    select: (data) => data.runs,
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.runs.some(
        (r) => r.status === "running" || r.status === "pending",
      );
      return hasRunning ? 3000 : false;
    },
  });
}

export function useTriggerAiRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId?: string) =>
      api.post<{ runId: string }>("/admin/ai-agent/run", sourceId ? { sourceId } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-agent", "runs"] });
      qc.invalidateQueries({ queryKey: ["ai-agent", "sources"] });
    },
  });
}

// ---- Events ----

interface AiEventsFilters {
  status?: string;
  sourceId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useAiEvents(filters: AiEventsFilters = {}) {
  const params: Record<string, string> = {};
  if (filters.status) params.status = filters.status;
  if (filters.sourceId) params.sourceId = filters.sourceId;
  if (filters.search) params.search = filters.search;
  if (filters.limit) params.limit = String(filters.limit);
  if (filters.offset) params.offset = String(filters.offset);

  return useQuery({
    queryKey: ["ai-agent", "events", filters],
    queryFn: () => api.get<{ events: AiEvent[]; total: number }>("/admin/ai-agent/events", params),
  });
}

export interface AiFestivalOption {
  id: string;
  kind: "proposal" | "event";
  title: string;
  status: string;
}

export function useAiFestivalOptions(enabled: boolean) {
  return useQuery({
    queryKey: ["ai-agent", "festival-options"],
    enabled,
    queryFn: () => api.get<{ festivals: AiFestivalOption[] }>("/admin/ai-agent/festival-options"),
    select: (data) => data.festivals,
  });
}

export function useApproveAiEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      categoryId: string;
      venueId?: string;
      parentProposalId?: string | null;
      parentEventId?: string | null;
      ticketPrice?: number | null;
      posterUrl?: string | null;
      posterSha256?: string;
      title?: string;
      eventDescription?: string;
      date?: string;
      endDate?: string | null;
      isAllDay?: boolean;
      detailsUrl?: string;
      registrationOptions?: RegistrationOption[];
      registrationNotes?: string;
      registrationType?: string | null;
      registrationValue?: string | null;
      source?: string;
      sourceUrl?: string;
    }) =>
      api.put<{ eventId: string; notificationWarning?: boolean }>(
        `/admin/ai-agent/events/${id}/approve`,
        body,
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["ai-agent", "events"] }),
        qc.invalidateQueries({ queryKey: ["events"] }),
        qc.invalidateQueries({ queryKey: ["ai-agent", "festival-options"] }),
        qc.invalidateQueries({ queryKey: ["parent-events"] }),
        qc.invalidateQueries({ queryKey: ["admin-stats"] }),
      ]),
  });
}

export function useRejectAiEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put<{ ok: boolean }>(`/admin/ai-agent/events/${id}/reject`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "events"] }),
  });
}

// ---- Rules ----

export type MatchOperator =
  | { contains: string }
  | { startsWith: string }
  | { equals: string }
  | { regex: string };

export type MatchableField =
  | "title"
  | "description"
  | "locationName"
  | "locationAddress"
  | "sourceUrl";

export type RuleMatch = Partial<Record<MatchableField, MatchOperator>>;

export interface RuleOverrides {
  suggestedCategory?: string | null;
  suggestedVenue?: string | null;
  registrationType?: "phone" | "email" | "url" | "ticket_url" | "other" | null;
  registrationValue?: string | null;
  ticketPrice?: number | null;
  locationName?: string;
  locationAddress?: string | null;
  confidence?: number;
}

export interface AiSourceRule {
  id: string;
  sourceId: string;
  name: string;
  enabled: boolean;
  priority: number;
  match: RuleMatch;
  overrides: RuleOverrides;
  createdAt: string;
  updatedAt: string;
}

export function useAiSourceRules(sourceId: string | null) {
  return useQuery({
    queryKey: ["ai-agent", "sources", sourceId, "rules"],
    enabled: !!sourceId,
    queryFn: () => api.get<{ rules: AiSourceRule[] }>(`/admin/ai-agent/sources/${sourceId}/rules`),
    select: (data) => data.rules,
  });
}

export function useCreateAiSourceRule(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      enabled?: boolean;
      priority?: number;
      match?: RuleMatch;
      overrides?: RuleOverrides;
    }) => api.post<AiSourceRule>(`/admin/ai-agent/sources/${sourceId}/rules`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "sources", sourceId, "rules"] }),
  });
}

export function useUpdateAiSourceRule(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      enabled?: boolean;
      priority?: number;
      match?: RuleMatch;
      overrides?: RuleOverrides;
    }) => api.put<AiSourceRule>(`/admin/ai-agent/sources/${sourceId}/rules/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "sources", sourceId, "rules"] }),
  });
}

export function useDeleteAiSourceRule(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/ai-agent/sources/${sourceId}/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "sources", sourceId, "rules"] }),
  });
}

export function useBulkRejectAiEvents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ results: Array<{ id: string; ok: boolean }> }>(
        "/admin/ai-agent/events/reject-bulk",
        { ids },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent", "events"] }),
  });
}
